// Practice-tool maths (pure, no React, no audio): the progressive speed
// trainer's schedule. GP-style semantics: play the form at `fromPct` of the
// arrangement tempo, and after every `passesPerStep` passes move `stepPct`
// toward `toPct`, clamping there. Works ascending (70→100) or descending
// (110→90); step is a magnitude, direction comes from from→to.

export interface TrainerConfig {
  enabled: boolean;
  /** Starting speed, % of the arrangement tempo. */
  fromPct: number;
  /** Target speed, % — the schedule clamps here. */
  toPct: number;
  /** Percentage-point change per step (magnitude; min 1). */
  stepPct: number;
  /** Form passes played at each speed before stepping (min 1). */
  passesPerStep: number;
}

export const DEFAULT_TRAINER: TrainerConfig = {
  enabled: false,
  fromPct: 70,
  toPct: 100,
  stepPct: 10,
  passesPerStep: 1,
};

/** Speed (%) for a 1-based form pass under a trainer schedule. */
export function trainerPctForPass(pass: number, cfg: TrainerConfig): number {
  const per = Math.max(1, Math.floor(cfg.passesPerStep));
  const step = Math.max(1, Math.abs(cfg.stepPct));
  const steps = Math.floor((Math.max(1, Math.floor(pass)) - 1) / per);
  const dir = Math.sign(cfg.toPct - cfg.fromPct);
  const pct = cfg.fromPct + steps * step * dir;
  return dir >= 0 ? Math.min(cfg.toPct, pct) : Math.max(cfg.toPct, pct);
}

/** Passes needed to reach (and hold) the target speed. */
export function trainerPassesToTarget(cfg: TrainerConfig): number {
  const per = Math.max(1, Math.floor(cfg.passesPerStep));
  const step = Math.max(1, Math.abs(cfg.stepPct));
  const distance = Math.abs(cfg.toPct - cfg.fromPct);
  return (Math.ceil(distance / step)) * per + 1;
}
